// ──────────────────────────────────────────────────────────────
//  UserFormModal.js
// ──────────────────────────────────────────────────────────────
import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { isValidEmail, isValidPhone } from '../../Shared/functions';

/**
 * Props
 *  - open        : bool               → control del modal
 *  - onClose     : () => void         → cerrar el modal
 *  - onSubmit    : (data) => Promise → llamada cuando el user pulsa "Crear/Actualizar"
 *  - initialValues (optional)          → datos para “editar”
 */
export default function UserFormModal({
  open,
  onClose,
  onSubmit,
  initialValues = {},
}) {
  const [values, setValues] = useState({
    identification: '',
    firstName: '',
    middleName: '',
    firstLastName: '',
    middleLastName: '',
    gender: '',
    phone: '',
    email: '',
    address: '',
  });

  console.log("el initial values", initialValues)

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // -----------------------------------------------------------------
  //  Cambiar el valor de un campo
  // -----------------------------------------------------------------
  const handleChange = (field) => (e) => {
    setValues((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined })); // limpiar error
  };

  const emptyForm = {
    identification: '',
    firstName: '',
    middleName: '',
    firstLastName: '',
    middleLastName: '',
    gender: '',
    phone: '',
    email: '',
    address: '',
  };

  useEffect(() => {
    if(initialValues){
        const form = {
            identification: initialValues.identificationUser,
            firstName: initialValues.firstName,
            middleName: initialValues.middleName,
            firstLastName: initialValues.lastName,
            middleLastName: initialValues.middleLastName,
            gender: initialValues.gender,
            phone: initialValues.phone,
            email: initialValues.userEmail,
            address: initialValues.address,
          };
          setValues(form);
    }
  }, []);


  // -----------------------------------------------------------------
  //  Validación rápida (puedes ampliarla)
  // -----------------------------------------------------------------
  const validate = () => {
    const newErr = {};

    if (!values.identification) newErr.identification = 'Requerido';
    if (!values.firstName) newErr.firstName = 'Requerido';
    if (!values.firstLastName) newErr.firstLastName = 'Requerido';
    if (!values.gender) newErr.gender = 'Seleccione un género';
    if (!values.phone) newErr.phone = 'Requerido';
    else if (!isValidPhone(values.phone))
      newErr.phone = 'Sólo números (7‑15 dígitos)';

    if (!values.email) newErr.email = 'Requerido';
    else if (!isValidEmail(values.email))
      newErr.email = 'Formato de email inválido';

    if (!values.address) newErr.address = 'Requerido';

    setErrors(newErr);
    return Object.keys(newErr).length === 0;
  };

  // -----------------------------------------------------------------
  //  Envío del formulario
  // -----------------------------------------------------------------
  const trim = (v) => String(v ?? '').trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = {
      ...values,
      identification: trim(values.identification),
      firstName: trim(values.firstName),
      firstLastName: trim(values.firstLastName),
      email: trim(values.email),
      address: trim(values.address),
      phone: [trim(values.phone)],
      idHeaderFine: initialValues.idHeaderFine,
    };
    setSaving(true);
    try {
      await onSubmit(payload); // puedes devolver una promesa o nada
      setValues(emptyForm)
      onClose(); // cerrar solo si el submit fue ok
    } catch (err) {
      console.error('Error al guardar:', err);
      // Aquí podrías mapear errores del backend a `setErrors`
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------------------------------------------
  //  Render
  // -----------------------------------------------------------------
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        {/* Fondo oscuro */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-50"
          leave="ease-in duration-200"
          leaveFrom="opacity-50"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        {/* Modal centrado */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-2xl overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between bg-gray-100 px-4 py-3">
                  <Dialog.Title className="text-lg font-medium text-gray-900">
                    Actualizar datos personales
                  </Dialog.Title>
                  <button
                    type="button"
                    className="rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>

                {/* FORM */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                  {/* IDENTIFICACIÓN */}
                  <div>
                    <label htmlFor="identification" className="block text-sm font-medium text-gray-700">
                      Identificación <span className="text-red-600">*</span>
                    </label>
                    <input
                      id="identification"
                      type="text"
                      value={values.identification}
                      onChange={handleChange('identification')}
                      className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                    />
                    {errors.identification && (
                      <p className="mt-1 text-sm text-red-600">{errors.identification}</p>
                    )}
                  </div>

                  {/* NOMBRES */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-1">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                        Nombres <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="firstName"
                        type="text"
                        value={values.firstName}
                        onChange={handleChange('firstName')}
                        className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                      />
                      {errors.firstName && (
                        <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                      )}
                    </div>
                  </div>

                  {/* APELLIDOS */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-1">
                    <div>
                      <label htmlFor="firstLastName" className="block text-sm font-medium text-gray-700">
                       Apellidos <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="firstLastName"
                        type="text"
                        value={values.firstLastName}
                        onChange={handleChange('firstLastName')}
                        className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                      />
                      {errors.firstLastName && (
                        <p className="mt-1 text-sm text-red-600">{errors.firstLastName}</p>
                      )}
                    </div>
                  </div>

                  {/* GÉNERO & TELÉFONO */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="gender" className="block text-sm font-medium text-gray-700">
                        Género <span className="text-red-600">*</span>
                      </label>
                      <select
                        id="gender"
                        value={values.gender}
                        onChange={handleChange('gender')}
                        className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                      >
                        <option value="">Seleccionar</option>
                        <option value="MASCULINO">MASCULINO</option>
                        <option value="FEMENINO">FEMENINO</option>
                      </select>
                      {errors.gender && (
                        <p className="mt-1 text-sm text-red-600">{errors.gender}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                        Teléfono <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        value={values.phone}
                        onChange={handleChange('phone')}
                        className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                      />
                      {errors.phone && (
                        <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                      )}
                    </div>
                  </div>

                  {/* EMAIL */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email <span className="text-red-600">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={values.email}
                      onChange={handleChange('email')}
                      className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>

                  {/* DIRECCIÓN */}
                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                      Dirección <span className="text-red-600">*</span>
                    </label>
                    <textarea
                      id="address"
                      rows={3}
                      value={values.address}
                      onChange={handleChange('address')}
                      className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                    />
                    {errors.address && (
                      <p className="mt-1 text-sm text-red-600">{errors.address}</p>
                    )}
                  </div>

                  {/* BOTONES */}
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
                      onClick={onClose}
                      disabled={saving}
                    >
                      Cancelar
                    </button>

                    <button
                      type="submit"
                      disabled={saving}
                      className="inline-flex items-center rounded-md bg-indigo-950 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                    >
                      {saving && (
                        <svg
                          className="h-5 w-5 animate-spin text-white mr-2"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8z"
                          />
                        </svg>
                      )}
                      Actualizar
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}


